import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline,
  EmailOutlined,
  ErrorOutline,
  InfoOutlined,
  TaskAlt,
  ThumbDownAlt,
  Visibility,
} from "@mui/icons-material";
import React, { useMemo } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { ActionData } from "./types";
import { Can } from "src/helpers/Auth";
import StatusChip from "./StatusChip";
import getStyles from "./ActionsTableStyles.ts";
import { useTheme } from "@mui/material/styles";

interface ActionsTableProps {
  isLoading: boolean;
  processedData: ActionData[];
  actionIdNumber: number | null;
  sortField: string;
  sortDirection: string;
  handleSortChange: (field: keyof ActionData) => void;
  handleOpenModal: (data: any, title: string) => void;
  handleApprove: (id: number) => Promise<void>;
  handleReject: (id: number) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  getActionTypeName: (fullPath: string) => string;
  page: number;
  totalPages: number;
  handlePageChange: (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => void;
  actions: ActionData[];
}

const ActionsTable: React.FC<ActionsTableProps> = ({
  isLoading,
  processedData,
  actionIdNumber,
  sortField,
  sortDirection,
  handleSortChange,
  handleOpenModal,
  handleApprove,
  handleReject,
  handleDelete,
  getActionTypeName,
  page,
  totalPages,
  handlePageChange,
  actions,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const pageSize =
    actions.length > 0 ? Math.ceil(actions.length / totalPages) : 10;
  const columnHelper = createColumnHelper<ActionData>();

  // Get email recipients from data (handling different formats)
  const getEmailRecipients = (data: any) => {
    if (!data) return "-";

    if (data.email) {
      return data.email;
    } else if (data.recipients && Array.isArray(data.recipients)) {
      return data.recipients.join(", ");
    }

    return "-";
  };

  // Get email subject from data
  const getEmailSubject = (data: any) => {
    return data?.subject || "-";
  };

  // Get email action details
  const getActionDetails = (row: ActionData) => {
    switch (row.action_summary?.toLowerCase()) {
      case "send_email":
      case "send email":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailOutlined fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight="medium">
                {getActionTypeName(row.action_summary)}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              To: {getEmailRecipients(row.data)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "200px",
              }}
            >
              Subject: {getEmailSubject(row.data)}
            </Typography>
          </Box>
        );
      default:
        return (
          <Tooltip title={row.on_approval_action || row.action_summary}>
            <Typography variant="body2" fontWeight="medium">
              {getActionTypeName(row.action_summary as any)}
            </Typography>
          </Tooltip>
        );
    }
  };

  // Define table columns using columnHelper
  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: () => (
          <Box
            onClick={() => handleSortChange("id")}
            sx={styles.headerCell}
            className="center-align"
            data-attr="assistant-actions:btn:sort-by-id"
          >
            ID
            {sortField === "id" && (
              <Box component="span" ml={0.5}>
                {sortDirection === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Box>
        ),
        cell: ({ row }) => <Box sx={styles.idBadge}>{row.original.id}</Box>,
      }),
      columnHelper.accessor("action_summary", {
        header: () => (
          <Box
            onClick={() => handleSortChange("on_approval_action")}
            sx={styles.headerCell}
            data-attr="assistant-actions:btn:sort-by-action-details"
          >
            Action Details
            {sortField === "on_approval_action" && (
              <Box component="span" ml={0.5}>
                {sortDirection === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Box>
        ),
        cell: ({ row }) => getActionDetails(row.original),
      }),
      columnHelper.accessor("data", {
        header: () => (
          <Box sx={{ ...styles.headerCell, justifyContent: "center" }}>
            Data
          </Box>
        ),
        cell: ({ row }) => (
          <Box sx={styles.centerContent}>
            <Tooltip title="View detailed data" arrow placement="top">
              <Button
                data-testid={`aa-act-btn-view-${row.original.id}`}
                data-attr={`assistant-actions:btn:view-action-data-${row.original.id}`}
                variant="outlined"
                size="small"
                startIcon={<Visibility />}
                onClick={() =>
                  handleOpenModal(row.original.data, "Data Details")
                }
                sx={styles.viewButton}
              >
                View
              </Button>
            </Tooltip>
          </Box>
        ),
      }),
      columnHelper.accessor("created_by", {
        header: () => (
          <Box
            onClick={() => handleSortChange("created_by")}
            sx={styles.headerCell}
            data-attr="assistant-actions:btn:sort-by-created-by"
          >
            Created By
            {sortField === "created_by" && (
              <Box component="span" ml={0.5}>
                {sortDirection === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Box>
        ),
        cell: ({ row }) => (
          <Box sx={styles.createdByContainer}>
            <Box sx={styles.createdByDot} />
            <Typography sx={styles.createdByText}>
              {row.original.created_by?.user_display_name || "-"}
            </Typography>
          </Box>
        ),
      }),
      columnHelper.accessor("create_date", {
        header: () => (
          <Box
            onClick={() => handleSortChange("create_date")}
            sx={styles.headerCell}
            data-attr="assistant-actions:btn:sort-by-created-date"
          >
            Created
            {sortField === "create_date" && (
              <Box component="span" ml={0.5}>
                {sortDirection === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Box>
        ),
        cell: ({ row }) => (
          <Typography sx={styles.dateText}>
            {row.original.create_date
              ? new Date(row.original.create_date).toLocaleString()
              : "-"}
          </Typography>
        ),
      }),
      columnHelper.accessor("status", {
        header: () => (
          <Box
            onClick={() => handleSortChange("status")}
            sx={{ ...styles.headerCell, justifyContent: "center" }}
            data-attr="assistant-actions:btn:sort-by-status"
          >
            Status
            {sortField === "status" && (
              <Box component="span" ml={0.5}>
                {sortDirection === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Box>
        ),
        cell: ({ row }) => (
          <Box sx={styles.centerContent}>
            <StatusChip
              data-testid={`aa-act-chip-status-${row.original.id}`}
              status={row.original.status}
              errorMessage={row.original.error_message}
              rejectionReason={row.original.rejection_reason}
            />
            {row.original.error_message && (
              <Tooltip title={row.original.error_message} arrow placement="top">
                <IconButton size="small" color="error" sx={{ ml: 1 }}>
                  <ErrorOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: () => (
          <Box sx={{ ...styles.headerCell, justifyContent: "flex-end" }}>
            Actions
          </Box>
        ),
        cell: ({ row }) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {row.original.status === "pending" && (
              <>
                <Can
                  perform="action:approve"
                  yes={() => (
                    <Tooltip title="Approve Action" arrow placement="top">
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        onClick={() => handleApprove(row.original.id)}
                        startIcon={<TaskAlt />}
                        sx={styles.actionButton}
                        data-attr={`assistant-actions:btn:approve-action-${row.original.id}`}
                      >
                        Approve
                      </Button>
                    </Tooltip>
                  )}
                  no={() => null}
                />
                <Can
                  perform="action:approve"
                  yes={() => (
                    <Tooltip title="Reject Action" arrow placement="top">
                      <Button
                        size="small"
                        color="warning"
                        variant="contained"
                        onClick={() => handleReject(row.original.id)}
                        startIcon={<ThumbDownAlt />}
                        sx={styles.actionButton}
                        data-attr={`assistant-actions:btn:reject-action-${row.original.id}`}
                      >
                        Reject
                      </Button>
                    </Tooltip>
                  )}
                  no={() => null}
                />
              </>
            )}
            <Can
              perform="action:delete"
              yes={() => (
                <Tooltip title="Delete Action" arrow placement="top">
                  <Button
                    data-testid={`aa-act-btn-delete-${row.original.id}`}
                    data-attr={`assistant-actions:btn:delete-action-${row.original.id}`}
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={() => handleDelete(row.original.id)}
                    startIcon={<DeleteOutline />}
                    sx={styles.actionButton}
                  >
                    Delete
                  </Button>
                </Tooltip>
              )}
              no={() => null}
            />
          </Stack>
        ),
      }),
    ],
    [
      sortField,
      sortDirection,
      theme,
      handleSortChange,
      getActionTypeName,
      handleOpenModal,
      handleApprove,
      handleReject,
      handleDelete,
    ],
  );

  // Initialize table instance
  const table = useReactTable({
    data: processedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize: pageSize,
      },
    },
  });

  if (isLoading) {
    return (
      <Box sx={styles.loadingContainer} data-testid="actions-table-loading">
        <CircularProgress size={40} thickness={4} />
        <Typography variant="subtitle1" color="text.secondary" ml={2}>
          Loading actions...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.tableContainer} data-testid="actions-table-container">
      <TableContainer>
        <Table data-testid="actions-table">
          <TableHead>
            <TableRow sx={styles.headerRow}>
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      ...styles.tableHeaderCell,
                      textAlign:
                        header.id === "id" ||
                        header.id === "status" ||
                        header.id === "data"
                          ? "center"
                          : header.id === "actions"
                            ? "right"
                            : "left",
                      display: ["data", "created_by", "create_date"].includes(
                        header.id,
                      )
                        ? { xs: "none", md: "table-cell" }
                        : header.id === "action_summary"
                          ? { xs: "none", sm: "table-cell" }
                          : "table-cell",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableCell>
                )),
              )}
            </TableRow>
          </TableHead>
          <TableBody data-testid="actions-table-body">
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => {
                const isHighlighted = row.original.id === actionIdNumber;
                const isEven = parseInt(row.id) % 2 === 0;

                return (
                  <TableRow
                    key={row.id}
                    data-testid={`actions-table-row-${row.original.id}`}
                    // @ts-ignore
                    sx={{
                      ...styles.tableRow,
                      ...(isEven && styles.evenRow),
                      ...(isHighlighted && styles.highlightedRow),
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        sx={{
                          ...styles.tableCell,
                          textAlign:
                            cell.column.id === "id" ||
                            cell.column.id === "status" ||
                            cell.column.id === "data"
                              ? "center"
                              : cell.column.id === "actions"
                                ? "right"
                                : "left",
                          display: [
                            "data",
                            "created_by",
                            "create_date",
                          ].includes(cell.column.id)
                            ? { xs: "none", md: "table-cell" }
                            : cell.column.id === "action_summary"
                              ? { xs: "none", sm: "table-cell" }
                              : "table-cell",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  sx={styles.noDataCell}
                >
                  <Box sx={styles.noDataContainer}>
                    <Box sx={styles.noDataIconContainer}>
                      <InfoOutlined color="action" />
                    </Box>
                    <Typography
                      variant="subtitle1"
                      color="text.primary"
                      fontWeight={500}
                      mb={0.5}
                    >
                      No Actions Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      There are currently no external actions to display.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {actions.length > 0 && (
        <Box sx={styles.paginationContainer}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
            color="primary"
            siblingCount={1}
            size="small"
            data-attr="assistant-actions:btn:change-page"
          />
        </Box>
      )}
    </Box>
  );
};

export default ActionsTable;
